import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ReturnToTop } from '../../FooterComponents/ReturnToTop'
import './termsofuse.css'
import PageTitleHook from '../../../utils/PageTitleHook/PageTitleHook'
import { headerSection, createOrderSublist, createCustomListItems } from '../../../utils/helperListFunctions/listHelper'
import { purpose, governingLaws, tradeMarksAndLogos, rulesOfBehavior, terminationOfAccess, linksToOtherWebSites, changesToTermsOfUse, acknowledgment, terminationHeader } from '../../../constants/TermsOfUseBullets'

const TermsOfUsePage = () => {
  PageTitleHook('USCIS Online Account | Legal')
  const location = useLocation()
  const securityConsentRef = useRef<HTMLDivElement>(null)
  const dhsPrivacyRef = useRef<HTMLDivElement>(null)
  const fbiPrivacyRef = useRef<HTMLDivElement>(null)
  const paperworkReductionRef = useRef<HTMLDivElement>(null)
  const tosRef = useRef<HTMLDivElement>(null)
  const [prevAnchor, setPrevAnchor] = useState('')

  useEffect(() => {
    if (location.hash !== prevAnchor) {
      setPrevAnchor(location.hash)
      switch (location.hash) {
        case '#tos-dhs-privacy':
          dhsPrivacyRef.current!.scrollIntoView()
          break;
        case '#tos-fbi-privacy':
          fbiPrivacyRef.current!.scrollIntoView()
          break;
        case '#tos-paperwork-reduction':
          paperworkReductionRef.current!.scrollIntoView()
          break;
        case '#tos-tos':
          tosRef.current!.scrollIntoView()
          break
        default:
          securityConsentRef.current!.scrollIntoView()
      }
    }
  }, [prevAnchor, location])

  
  return (
    <div data-testid="terms-of-use-container">
      <div className=" max-w-4xl rounded-sm overflow-auto shadow-md mx-auto bg-white my-0 p-6 ">
        <Link to="/sign-in" className="nav-link mx-0 px-0">
          Return to Sign In
        </Link>
        <div className="my-6" ref={securityConsentRef}>
          <h3 className="terms-heading" data-testid="tos-security-consent" id="tos-security-consent">
            Department of Homeland Security Consent
          </h3>
          <p>
            You are about to access a Department of Homeland Security computer system. This computer system and data
            therein are the property of the U.S. Government and provided for official U.S. Government information and
            use. There is no expectation of privacy when you use this computer system. The use of a password or any
            other security measure does not establish an expectation of privacy. By using this system, you consent to
            the terms set forth in this notice. You may not process classified national security information on this
            computer system. Access to this system is restricted to authorized users only. Unauthorized access, use, or
            modification of this system or of data contained herein, or in transit to/from this system, may constitute a
            violation of section 1030 of title 18 of the U.S. Code and other criminal laws. Anyone who accesses a
            Federal computer system without authorization or exceeds access authority, or obtains, alters, damages,
            destroys, or discloses information, or prevents authorized use of information on the computer system, may be
            subject to penalties, fines, or imprisonment. This computer system and any related equipment is subject to
            monitoring for administrative oversight, law enforcement, criminal investigative purposes, inquiries into
            alleged wrongdoing or misuse, and to ensure proper performance of applicable security features and
            procedures. DHS may conduct monitoring activities without further notice.
          </p>
          <ReturnToTop />
        </div>
        <div className="my-6" ref={dhsPrivacyRef}>
          <h3 className="terms-heading" data-testid="tos-dhs-privacy" id="tos-dhs-privacy">
            DHS Privacy Notice
          </h3>
          <div>
            <p className="font-bold terms-paragraph">Authorities</p>
            <p>
              The information requested on this form is collected under the Immigration and Nationality Act, as amended;
              the Homeland Security Act of 2002 (Public Law 107-296); the E-Government Act of 2002 (Public Law 107-347);
              the Government Paperwork Elimination Act (Public Law 105-277); and Title 8 of the Code of Federal
              Regulations (CFR).
            </p>

            <p className="font-bold terms-paragraph">Purpose</p>
            <p>
              The primary purpose of providing the requested information on this form is to create or update your USCIS
              Online Account, also referred to as MyAccount, to give you access to USCIS's online services.
            </p>

            <p className="font-bold terms-paragraph">Disclosure</p>
            <p>
              The information you provide is voluntary. However, failure to provide information may prevent you from
              creating a USCIS Online Account, which will limit your ability to access and use USCIS's online services.
            </p>

            <p className="font-bold terms-paragraph">Routine Uses</p>
            {
              <p>
                DHS may share information you provide on this form with other Federal, state, local, and foreign
                government agencies and authorized organizations. DHS follows approved routine uses described in the
                associated published system of records notice [DHS/ALL-037 E-Authentication Records System of Records]
                and specific system or program published privacy impact assessments, which you can find at
                <a
                  className="nav-link"
                  aria-label="www.dhs.gov/privacy"
                  href="https://www.dhs.gov/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.dhs.gov/privacy.
                </a>
                DHS may also share this information, as appropriate, for law enforcement purposes or in the interest of
                national security.
              </p>
            }
          </div>
          <ReturnToTop />
        </div>
        <div className="my-6" ref={fbiPrivacyRef}>
          <h3 className="terms-heading" data-testid="tos-fbi-privacy" id="tos-fbi-privacy">
            FBI Privacy Notice
          </h3>
          <div>
            <p>
              USCIS may use your biometrics to obtain the criminal history records of the Federal Bureau of
              Investigation (FBI), for identity verification, to determine eligibility, to create immigration documents
              (for example, Permanent Resident Card, Employment Authorization Document), or any purpose authorized by
              the Immigration and Nationality Act. You may obtain a copy of your own FBI record using the procedures
              outlined at 28 CFR 16.30-16.34. For more information, please visit:
              <a
                href="https://www.fbi.gov/services/cjis/compact-council/guiding-principles-noncriminal-justice-applicants-privacy-rights"
                className="nav-link"
              >
                https://www.fbi.gov/services/cjis/compact-council/guiding-principles-noncriminal-justice-applicants-privacy-rights.
              </a>
            </p>
            <p className="terms-paragraph">
              For information regarding how the FBI will use your fingerprints, please visit:
              <a href="https://www.fbi.gov/services/cjis/compact-council/privacy-act-statement" className="nav-link">
                https://www.fbi.gov/services/cjis/compact-council/privacy-act-statement.
              </a>
            </p>
          </div>
          <ReturnToTop />
        </div>
        <div className="my-6" ref={paperworkReductionRef}>
          <h3 className="terms-heading" data-testid="tos-paperwork-reduction" id="tos-paperwork-reduction">
            Paperwork Reduction Act Burden Disclosure Notice
          </h3>
          <div>
            <p>
              USCIS may not conduct or sponsor an information collection, and you are not required to respond to a
              collection of information, unless it displays a currently valid Office of Management and Budget (OMB)
              control number. The public reporting burden for this collection of information is estimated at 0.167 hours
              per response, including the time for reviewing instructions, gathering the required documentation and
              information, and completing and submitting the request. Send comments regarding this burden estimate or
              any other aspect of this collection of information, including suggestions for reducing this burden, to:{' '}
            </p>
            <div className="my-4">
              <p className="font-bold">U.S. Citizenship and Immigration Services</p>
              <p className="font-bold">Office of Policy and Strategy, Regulatory Coordination Division</p>
              <p className="font-bold">5900 Capital Gateway Drive, Mail Stop #2140</p>
              <p className="font-bold">Camp Springs, MD 20588-0009</p>
            </div>

            <div className="my-4">
              <p>OMB Control No. 1615-0122.</p>
              <p>Expiration Date: 9/30/2027</p>
            </div>

            <p>Do not mail your completed request to this address.</p>
          </div>
          <ReturnToTop />
        </div>
        <div className="my-6" ref={tosRef}>
          <h3 className="terms-heading" data-testid="tos-tos" id="tos-tos">
            Terms of Use - U.S. Citizenship and Immigration Services Online Electronic Immigration System
          </h3>
          <div>
            <ol  className="tos-ol terms-of-use-ol p-0">
                {headerSection(<h3>Purpose</h3>)}
                {createOrderSublist(<>{createCustomListItems(purpose, )}</>)}
                {headerSection(<h3>Governing Laws</h3>)}
                {createOrderSublist(<>{createCustomListItems(governingLaws)}</>)}
                {headerSection(<h3>Trademarks and Logos</h3>)}
                {createOrderSublist(<>{createCustomListItems(tradeMarksAndLogos)}</>)}
                {headerSection(<h3>Rules of Behavior</h3>)}
                {createOrderSublist(<>{createCustomListItems(rulesOfBehavior)}</>)}
                {headerSection(terminationHeader)}
                {createOrderSublist(<>{createCustomListItems(terminationOfAccess)}</>)}
                {headerSection(<h3>Links to Other Web Sites</h3>)}
                {createOrderSublist(<>{createCustomListItems(linksToOtherWebSites)}</>)}
                {headerSection(<h3>Changes to Terms of Use</h3>)}
                {createOrderSublist(<>{createCustomListItems(changesToTermsOfUse)}</>)}
                {headerSection(<h3>Acknowledgment</h3>)}
                {createOrderSublist(<>{createCustomListItems(acknowledgment)}</>)}
            </ol>
          </div>
          <ReturnToTop />
        </div>
      </div>
    </div>
  )
}

export default TermsOfUsePage